import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SEOHead } from '../components/SEOHead';
import { getArticleBySlug, getRelatedArticles } from '../data/blogArticles';
import { Clock, Calendar, ArrowRight, ArrowLeft, Share2, BookOpen } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const BlogArticle = () => {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);
  
  if (!article) {
    return <Navigate to="/blog" replace />;
  }
  
  const relatedArticles = getRelatedArticles(slug, 3);

  // Custom renderers for markdown
  const components = {
    h2: ({ children }) => (
      <h2 
        className="text-2xl md:text-3xl font-bold text-white mt-12 mb-6 uppercase tracking-tight"
        style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
      >
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 
        className="text-xl md:text-2xl font-bold text-emerald-400 mt-8 mb-4"
        style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
      >
        {children}
      </h3>
    ),
    p: ({ children }) => (
      <p className="text-slate-300 leading-relaxed mb-6">
        {children}
      </p>
    ),
    ul: ({ children }) => (
      <ul className="list-disc list-inside space-y-2 text-slate-300 mb-6 ml-4">
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal list-inside space-y-2 text-slate-300 mb-6 ml-4">
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li className="text-slate-300">{children}</li>
    ),
    strong: ({ children }) => (
      <strong className="text-white font-semibold">{children}</strong>
    ),
    a: ({ href, children }) => (
      <Link 
        to={href} 
        className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2"
      >
        {children}
      </Link>
    ),
    table: ({ children }) => (
      <div className="overflow-x-auto mb-6">
        <table className="w-full border-collapse">
          {children}
        </table>
      </div>
    ),
    th: ({ children }) => (
      <th className="bg-slate-800 text-emerald-400 font-bold text-left p-3 border border-slate-700">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="text-slate-300 p-3 border border-slate-700">
        {children}
      </td>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-emerald-500 pl-4 py-2 my-6 bg-slate-800/30 rounded-r-lg">
        {children}
      </blockquote>
    )
  };

  return (
    <div className="min-h-screen" data-testid="blog-article-page">
      <SEOHead />
      
      {/* Hero Image */}
      <div className="relative h-[50vh] min-h-[400px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${article.image})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/30" />
      </div>
      
      {/* Article Content */}
      <article 
        className="relative -mt-32 z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8"
        itemScope 
        itemType="https://schema.org/BlogPosting"
      >
        {/* Breadcrumbs */}
        <Breadcrumbs 
          items={[
            { label: 'Blog', href: '/blog' },
            { label: article.title }
          ]} 
        />
        
        {/* Article Header */}
        <header className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-3 py-1 bg-orange-500/20 text-orange-400 text-sm font-bold uppercase rounded">
              {article.category}
            </span>
            <span className="flex items-center gap-1 text-slate-400 text-sm">
              <Clock className="w-4 h-4" />
              {article.readTime} de lectura
            </span>
          </div>
          
          <h1 
            className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white uppercase tracking-tight mb-6"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            itemProp="headline"
          >
            {article.title}
          </h1>
          
          <p 
            className="text-xl text-slate-300 mb-6"
            itemProp="description"
          >
            {article.excerpt}
          </p>
          
          <div className="flex items-center justify-between border-t border-b border-slate-800 py-4">
            <time 
              className="flex items-center gap-2 text-slate-400 text-sm"
              dateTime={article.publishDate}
              itemProp="datePublished"
            >
              <Calendar className="w-4 h-4" />
              Publicado el {new Date(article.publishDate).toLocaleDateString('es-ES', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
              })}
            </time>
            <button 
              className="flex items-center gap-2 text-slate-400 hover:text-emerald-400 transition-colors text-sm"
              onClick={() => navigator.share?.({ title: article.title, url: window.location.href })}
            >
              <Share2 className="w-4 h-4" />
              Compartir
            </button>
          </div>
        </header>

        {/* Ad Space Placeholder - After Header */}
        {/* <div className="my-8 p-4 border-2 border-dashed border-slate-700 rounded-xl text-center text-slate-500 text-sm">
          <span className="opacity-50">Espacio publicitario</span>
        </div> */}

        {/* Article Body */}
        <div 
          className="prose prose-invert prose-lg max-w-none"
          itemProp="articleBody"
        >
          <ReactMarkdown components={components}>
            {article.content}
          </ReactMarkdown>
        </div>

        {/* Internal Links Section */}
        {article.relatedLinks && article.relatedLinks.length > 0 && (
          <section className="mt-12 p-6 bg-slate-800/50 rounded-xl border border-slate-700">
            <h3 
              className="text-xl font-bold text-white mb-4 flex items-center gap-2"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              <BookOpen className="w-5 h-5 text-emerald-400" />
              Sigue Aprendiendo
            </h3>
            <div className="flex flex-wrap gap-3">
              {article.relatedLinks.map((link, index) => (
                <Link 
                  key={index}
                  to={link.href}
                  className="px-4 py-2 bg-slate-700 hover:bg-emerald-500/20 text-slate-300 hover:text-emerald-400 rounded-lg text-sm transition-colors"
                >
                  {link.text}
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Ad Space Placeholder - Middle */}
        {/* <div className="my-12 p-4 border-2 border-dashed border-slate-700 rounded-xl text-center text-slate-500 text-sm">
          <span className="opacity-50">Espacio publicitario</span>
        </div> */}

        {/* Related Articles */}
        <section className="mt-16">
          <h2 
            className="text-2xl font-bold text-white mb-8 uppercase"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Artículos Relacionados
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {relatedArticles.map(related => (
              <Link 
                key={related.id}
                to={`/blog/${related.slug}`}
                className="group"
              >
                <div className="relative overflow-hidden rounded-xl mb-3">
                  <img 
                    src={related.image} 
                    alt={related.title}
                    className="w-full aspect-video object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <h3 
                  className="text-lg font-bold text-white group-hover:text-emerald-400 transition-colors line-clamp-2"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {related.title}
                </h3>
                <span className="text-slate-500 text-xs flex items-center gap-1 mt-2">
                  <Clock className="w-3 h-3" />
                  {related.readTime}
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* Navigation */}
        <nav className="mt-16 flex items-center justify-between border-t border-slate-800 pt-8">
          <Link 
            to="/blog"
            className="flex items-center gap-2 text-slate-400 hover:text-emerald-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al Blog
          </Link>
          <Link 
            to="/"
            className="flex items-center gap-2 text-slate-400 hover:text-emerald-400 transition-colors"
          >
            Ir a la Guía
            <ArrowRight className="w-4 h-4" />
          </Link>
        </nav>
      </article>
      
      {/* Footer spacing */}
      <div className="h-20" />
    </div>
  );
};

export default BlogArticle;
